import { useState, type FormEvent } from 'react';
import { ApiError } from '@map/shared';
import { LoginPlanet } from './LoginPlanet.js';
import { login } from './api.js';
import { passkeysSupported, signInWithPasskey } from './passkey.js';

export interface LoginScreenProps {
  onSignedIn: () => void;
  /** Shown instead of the title when an expired session interrupted the user. */
  resuming?: boolean;
}

export function LoginScreen({ onSignedIn, resuming = false }: LoginScreenProps) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function attempt(fn: () => Promise<unknown>) {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      onSignedIn();
    } catch (err) {
      // Deliberately uninformative: never hint at how close a password was.
      setMessage(
        err instanceof ApiError && err.code === 'too_many_requests'
          ? 'Too many attempts. Try again shortly.'
          : 'That did not work',
      );
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!password || busy) return;
    void attempt(() => login(password));
  }

  return (
    <div className="relative flex h-full items-center justify-center overflow-hidden">
      <LoginPlanet />

      <form
        onSubmit={onSubmit}
        className="relative z-10 flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-4 rounded-[var(--radius-lg)] p-6"
        style={{
          background: 'color-mix(in oklch, var(--surface-0) 82%, transparent)',
          boxShadow: 'var(--shadow-panel)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <h1 className="text-lg font-medium">{resuming ? 'Welcome back' : 'map'}</h1>

        <label className="flex flex-col gap-2 text-sm">
          <span className="text-text-muted">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            className="rounded-[var(--radius-sm)] border border-border bg-surface-1 px-3 py-2 outline-none"
          />
        </label>

        <button
          type="submit"
          disabled={busy || password.length === 0}
          className="rounded-[var(--radius-sm)] bg-accent px-3 py-2 text-sm font-medium text-surface-0 disabled:opacity-50"
        >
          {busy ? 'Checking…' : 'Enter'}
        </button>

        {passkeysSupported() && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void attempt(signInWithPasskey)}
            className="rounded-[var(--radius-sm)] border border-border px-3 py-2 text-sm disabled:opacity-50"
          >
            Use a passkey
          </button>
        )}

        <p role="status" aria-live="polite" className="min-h-5 text-sm text-text-muted">
          {message}
        </p>
      </form>
    </div>
  );
}
